import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useAppSelector } from '../../app/hooks';
import strings, { selectLanguage } from '../../components/localize/localizeSlice';

import icoCar from '../../assets/images/ico_car.png';
import icoVanLight from '../../assets/images/ico_van_light.png';
import icoTruckLight from '../../assets/images/ico_truck_light.png';
import icoTruckHeavy from '../../assets/images/ico_truck_heavy.png';
import icoBike from '../../assets/images/ico_bike.png';
import icoWomo from '../../assets/images/ico_womo.png';
import icoTrailerLight from '../../assets/images/ico_trailer_light.png';
import icoTrailerHeavy from '../../assets/images/ico_trailer_heavy.png';
import icoBus from '../../assets/images/ico_bus.png';

export interface CarTypeFormData {
  make: string;
  model: string;
  width: string;
  height: string;
  length: string;
  weight: string;
  licensePlate: string;
  chassisNumber: string; // Added
  carType: string;
  carRegistered: boolean;
  specialOptions: string[];
  tires: "" | "Allwetter" | "Sommer" | "Winter";
}

interface CarTypeFormProps {
  onFormChange?: (isValid: boolean) => void;
  formData?: CarTypeFormData;
  setFormData?: (data: CarTypeFormData) => void;
}

const CarTypeForm = forwardRef(({ onFormChange, formData, setFormData }: CarTypeFormProps, ref) => {
  useAppSelector(selectLanguage);
  const [errors, setErrors] = useState({
    make: false,
    model: false,
    width: false,
    height: false,
    length: false,
    weight: false,
    licensePlate: false,
    carType: false,
    carRegistered: false,
  });

  const isValid = (currentErrors = errors): boolean => {
    return Object.values(currentErrors).every((error) => !error);
  };

  // Expose the validate function to the parent component
  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors = {
        make: !formData?.make,
        model: !formData?.model,
        width: !formData?.width,
        height: !formData?.height,
        length: !formData?.length,
        weight: !formData?.weight,
        licensePlate: !formData?.licensePlate,
        carType: !formData?.carType, 
        carRegistered: formData?.carRegistered === undefined, 
      };
      setErrors(newErrors);
      return isValid(newErrors);
    },
  }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (setFormData) {
      var updatedData = { ...formData, [name]: value, height: "0", width: "0", length: "0"} as CarTypeFormData;

      if (!updatedData.specialOptions.includes("RKZ") && !updatedData.carRegistered) {
        updatedData.specialOptions = [...updatedData.specialOptions, "RKZ"];
      }

      setFormData(updatedData);
    }
    const updatedErrors = {
      ...errors,
      [name]: !value,
    };
    setErrors(updatedErrors);
    if (onFormChange) {
      onFormChange(isValid(updatedErrors));
    }
  };

  const handleCarTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (setFormData) {
      var updatedData = { ...formData, carType: value } as CarTypeFormData;

      switch (value){
        case "LKW7.5": 
          updatedData = { ...updatedData, weight: "7501" } as CarTypeFormData;
          break;
        case "6": 
          updatedData = { ...updatedData, weight: "751" } as CarTypeFormData;
          break;
        default:
          updatedData = { ...updatedData, weight: "0" } as CarTypeFormData;
          break;
      }

      setFormData(updatedData);
    }

    setErrors((prev) => ({ ...prev, carType: false }));
    
    if (onFormChange) {
      onFormChange(true);
    }
  };

  return (
    <form className="p-4">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* Make */}
        <div>
          <label
            htmlFor="make"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.vehicleDetails.vehicleMake}
          </label>
          <input
            type="text"
            id="make"
            name="make"
            value={formData?.make || ""}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border ${
              errors.make ? "border-red-500" : "border-gray-300"
            } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
            placeholder="Toyota"
          />
          {errors.make && (
            <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: strings.form.vehicleDetails.vehicleMake})}</p>
          )}
        </div>

        {/* Model */}
        <div>
          <label
            htmlFor="model"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.vehicleDetails.vehicleModel}
          </label>
          <input
            type="text"
            id="model"
            name="model"
            value={formData?.model || ""}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border ${
              errors.model ? "border-red-500" : "border-gray-300"
            } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
            placeholder="Corolla"
          />
          {errors.model && (
            <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: strings.form.vehicleDetails.vehicleModel})}</p>
          )}
        </div>
      </div>

      {/* Chassis Number (Optional) */}
      <div className="mt-6">
        <label
          htmlFor="chassisNumber"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-2"
        >
          {strings.form.vehicleDetails.chassisNumber || "Fahrgestellnummer (optional)"}
        </label>
        <input
          type="text"
          id="chassisNumber"
          name="chassisNumber"
          value={formData?.chassisNumber || ""}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
          placeholder={strings.form.vehicleDetails.chassisNumberPlaceholder || "Fahrgestellnummer eingeben"}
        />
      </div>

      {/* Tire Type Dropdown */}
      <div className="mt-6">
        <label
          htmlFor="tires"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-2"
        >
          {strings.form.vehicleDetails.tireType}
        </label>
        <select
          id="tires"
          name="tires"
          value={formData?.tires || ""}
          onChange={(e) => {
            if (setFormData) {
              setFormData({ ...formData, tires: e.target.value as CarTypeFormData["tires"] } as CarTypeFormData);
            }
            setErrors((prev) => ({ ...prev, tires: false }));
            if (onFormChange) {
              onFormChange(true);
            }
          }}
          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm text-sm text-gray-700 dark:text-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 px-4 py-2"
        >
          <option value="" disabled>
            {strings.form.vehicleDetails.tireTypeSelect || "Bitte auswählen"}
          </option>
          <option value="Allwetter">{strings.form.vehicleDetails.tireTypeAllwetter || "Allwetter"}</option>
          <option value="Sommer">{strings.form.vehicleDetails.tireTypeSommer || "Sommer"}</option>
          <option value="Winter">{strings.form.vehicleDetails.tireTypeWinter || "Winter"}</option>
        </select>
      </div>

      {/* License Plate */}
      <div className="mt-6">
        <label
          htmlFor="licensePlate"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-2"
        >
          {strings.form.vehicleDetails.vehicleLicensePlate}
        </label>
        <input
          type="text"
          id="licensePlate"
          name="licensePlate"
          value={formData?.licensePlate || ""}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-md border ${
            errors.licensePlate ? "border-red-500" : "border-gray-300"
          } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
          placeholder={strings.form.vehicleDetails.vehicleLicensePlatePlaceHolder}
        />
        {errors.licensePlate && (
          <p className="mt-2 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: strings.form.vehicleDetails.vehicleLicensePlate})}</p>
        )}
      </div>

      {/* Car Type Selection */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-2">
          {strings.form.vehicleDetails.vehicleType}
        </label>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Pkw", value: "2", img: icoCar },
            { label: "Transporter bis 3,5T", value: "11", img: icoVanLight },
            { label: "Lkw bis 7,5T", value: "LKW7.5", img: icoTruckLight },
            { label: "Lkw ab 7,5T", value: "4", img: icoTruckHeavy },
            { label: "Motorrad", value: "1", img: icoBike },
            { label: "Wohnmobil", value: "10", img: icoWomo },
            { label: "Anhänger bis 750kg", value: "Anhänger750", img: icoTrailerLight },
            { label: "Anhänger ab 750kg", value: "6", img: icoTrailerHeavy },
            { label: "Bus", value: "8", img: icoBus }
            ].map((carType) => (
            <label
              key={carType.value}
              className="flex flex-col items-center text-center cursor-pointer"
            >
              <input
              type="radio"
              name="carType"
              value={carType.value}
              checked={formData?.carType === carType.value}
              onChange={handleCarTypeChange}
              className="hidden"
              />
              <img
              src={carType.img}
              alt={carType.label}
              className={`w-16 h-16 object-contain mb-2 ${
                formData?.carType === carType.value ? "ring-2 rounded-2xl ring-cuno-500 bg-gradient-to-r from-cuno-500 to-cyan-700  animate-pulse" : ""
              }`}
              />
              <span
              className={`text-sm ${
                formData?.carType === carType.value
                ? "text-gray-700 font-medium"
                : "text-gray-700 dark:text-gray-300"
              }`}
              >
              {carType.label}
              </span>
            </label>
            ))}
        </div>
        {errors.carType && (
          <p className="mt-2 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: strings.form.vehicleDetails.vehicleType})}</p>
        )}
      </div>

      {/* Car Value and Registration Row */}
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:gap-6">
        {/* Car Registration Toggle */}
        <div className="flex-1">
          <label
            htmlFor="carRegistered"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-2"
          >
            {strings.form.vehicleDetails.vehicleRegistered}
          </label>
          <div className="flex items-center justify-start">
            <input
              type="checkbox"
              id="carRegistered"
              name="carRegistered"
              checked={formData?.carRegistered || false}
              onChange={(e) => {
                if (setFormData) {
                  const updatedData = { ...formData };
                  if ((!updatedData.specialOptions?.includes("RKZ") || !updatedData.specialOptions) && !e.target.checked) {
                    updatedData.specialOptions = [...(updatedData.specialOptions ?? []), "RKZ"];
                  }
                  else if (updatedData.specialOptions?.includes("RKZ") && e.target.checked) {
                    updatedData.specialOptions = (updatedData.specialOptions ?? []).filter((opt) => opt !== "RKZ");
                  }

                  setFormData({ ...updatedData, carRegistered: e.target.checked } as CarTypeFormData);
                }
                setErrors((prev) => ({ ...prev, carRegistered: false }));
                if (onFormChange) {
                  onFormChange(true);
                }
              }}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
          </div>
          {errors.carRegistered && (
            <p className="mt-2 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: strings.form.vehicleDetails.vehicleRegistered})}</p>
          )}
        </div>
      </div>

      {/* Special Options Multi-Select */}
      <div className="mt-6">
        <label
          htmlFor="specialOptions"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-2"
        >
          {strings.form.vehicleDetails.vehicleAdditonalServices}
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: strings.form.vehicleDetails.vehicleAdditonalServicesOptions.redLicensePlates, value: "RKZ" },
            { label: strings.form.vehicleDetails.vehicleAdditonalServicesOptions.basicDetailing, value: "AW" },
            { label: strings.form.vehicleDetails.vehicleAdditonalServicesOptions.vehicleTeaching, value: "FEW" },
          ].map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                name="specialOptions"
                value={option.value}
                checked={formData?.specialOptions?.includes(option.value) || false}
                onChange={(e) => {
                  const { value, checked } = e.target;
                  if (setFormData) {
                    let carRegisteredState: boolean = false;

                    if (value === "RKZ" && !checked && !formData?.carRegistered) {
                      carRegisteredState = true;
                    }
                    else if (value === "RKZ" && checked && formData?.carRegistered) {
                      carRegisteredState = false;
                    }
                    else if (value !== "RKZ") {
                      carRegisteredState = formData?.carRegistered || false;
                    }

                    var updatedOptions = {
                        ...formData,
                        carRegistered: carRegisteredState,
                        specialOptions: checked
                          ? [...((formData?.specialOptions ?? [])), value]
                          : ((formData?.specialOptions ?? []).filter((opt) => opt !== value)),
                      } as CarTypeFormData
                      
                    setFormData(
                      updatedOptions
                    );
                  }
                  if (onFormChange) {
                    onFormChange(true);
                  }
                }}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </div>
    </form>
  );
});

export default CarTypeForm;